import { useState, useEffect, useRef } from 'react';
import { Button, Modal } from 'antd-v5';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRpcHL } from '@/constants/lang';
import getLocal from '@/app/utils/getLocal';
import styles from './index.module.scss';
import type { MysteryBoxGift } from '../../hooks/types';
import EFPicture from '@elem/ui/ef-picture';
import Gift from './Gift';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';

const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY as string;

interface MysteryBoxModalProps {
    open: boolean;
    onCancel?: () => void;
    onSubmit?: (recaptchaResponse: string) => Promise<boolean>;
    submitLoading?: boolean;
    submitError?: string | null;
    prizes?: MysteryBoxGift[];
    boxRuleName?: string;
}

type DrawPhase = 'idle' | 'opening' | 'revealed';

const ANIMATION_DURATION = 3000;
const BOX_COUNT = 3;

const BOX_IMG_ACTIVE = 'https://websiteoss.ecoflow.com/figma-uploads/1775034525884-gfguh9-compressed-Frame%202094655051.png';
const BOX_IMG_DEFAULT = 'https://websiteoss.ecoflow.com/figma-uploads/1775034531291-ca934l-compressed-Frame%202094655052.png';

// ─── 弹窗主体 ─────────────────────────────────────────────────────────────────

const MysteryBoxModal: React.FC<MysteryBoxModalProps> = ({ open, onCancel, onSubmit, submitLoading, submitError, prizes = [], boxRuleName = '' }) => {
    const { t } = useLang('MysteryBoxModal');
    const { locale } = getLocal();
    const hl = getRpcHL(locale);
    const [phase, setPhase] = useState<DrawPhase>('idle');
    const [activeBoxIndex, setActiveBoxIndex] = useState<number>(-1);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [recaptchaError, setRecaptchaError] = useState(false);

    const boxRuleNameMap = {
        焕新尊享盲盒: t('box_labels_1'),
        优享回馈盲盒: t('box_labels_2'),
        感恩陪伴盲盒: t('box_labels_3'),
    };

    // ─── 盒子标签（开箱前） ────────────────────────────────────────────────────────

    const BOX_LABELS = [t('box_labels_1'), t('box_labels_2'), t('box_labels_3')];
    const clearTimers = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    useEffect(() => {
        if (open) {
            [BOX_IMG_ACTIVE, BOX_IMG_DEFAULT].forEach((src) => {
                const img = new Image();
                img.src = src;
            });
        } else {
            const t = setTimeout(() => {
                setPhase('idle');
                setActiveBoxIndex(-1);
                setRecaptchaToken(null);
                setRecaptchaError(false);
                recaptchaRef.current?.reset();
            }, 300);
            return () => clearTimeout(t);
        }
    }, [open]);

    useEffect(() => {
        return () => clearTimers();
    }, []);

    const handleDraw = () => {
        if (phase !== 'idle') return;
        setPhase('opening');

        let currentIndex = 0;
        setActiveBoxIndex(currentIndex);

        // 动态调整间隔：开始快，结束慢（营造减速感）
        let elapsed = 0;
        const FAST_INTERVAL = 300;
        const SLOW_INTERVAL = 300;
        const SLOW_START = ANIMATION_DURATION * 0.65;

        const tick = () => {
            currentIndex = (currentIndex + 1) % BOX_COUNT;
            setActiveBoxIndex(currentIndex);
        };

        const scheduleNext = () => {
            const interval = elapsed < SLOW_START ? FAST_INTERVAL : SLOW_INTERVAL;
            elapsed += interval;
            if (elapsed >= ANIMATION_DURATION) {
                setActiveBoxIndex(-1);
                setPhase('revealed');
                return;
            }
            intervalRef.current = setTimeout(() => {
                tick();
                scheduleNext();
            }, interval);
        };

        scheduleNext();

        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_button',
            list_name: 'Service Renewal',
            button_name: t('open_your_mystery_box'),
            function: 'open_mystery_box',
        });
    };

    const handleOpenClick = async () => {
        if (!recaptchaToken) {
            setRecaptchaError(true);
            return;
        }
        setRecaptchaError(false);
        if (onSubmit) {
            const success = await onSubmit(recaptchaToken);
            if (!success) {
                recaptchaRef.current?.reset();
                setRecaptchaToken(null);
                return;
            }
        }
        handleDraw();
    };

    const count = Math.max(prizes.length, 1);

    return (
        <Modal
            className={styles.MysteryBoxModal}
            open={open}
            onCancel={phase === 'revealed' ? onCancel : undefined}
            footer={null}
            centered
            closable={phase === 'revealed'}
            maskClosable={false}
            title={null}
        >
            <div className={`mystery-inner phase-${phase}`}>
                {phase !== 'revealed' ? (
                    // ─── 开箱前 ───────────────────────────────────────────────
                    <>
                        <div className="modal-header">
                            <h2 className="modal-title">{t('title')}</h2>
                        </div>

                        <div className={`boxes-row count-${count} `}>
                            {[0, 1, 2].map((i) => (
                                <div
                                    key={i}
                                    className={[
                                        'box-card',
                                        phase === 'opening' ? 'is-opening' : '',
                                        phase === 'opening' && activeBoxIndex === i ? 'is-active' : '',
                                        phase === 'opening' && activeBoxIndex !== -1 && activeBoxIndex !== i ? 'is-dimmed' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    <div className="box-card-inner">
                                        <EFPicture src={phase === 'opening' && activeBoxIndex === i ? BOX_IMG_ACTIVE : BOX_IMG_DEFAULT} />
                                    </div>
                                    <p className="box-label">
                                        {(BOX_LABELS[i] ?? 'Mystery Box').split('\n').map((line, j) => (
                                            <span key={j}>
                                                {line}
                                                <br />
                                            </span>
                                        ))}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {phase === 'idle' && (
                            <div className="recaptcha-wrapper">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="normal"
                                    sitekey={SECRET_KEY}
                                    hl={hl}
                                    onChange={(token: string | null) => {
                                        setRecaptchaToken(token);
                                        if (token) setRecaptchaError(false);
                                    }}
                                    onExpired={() => setRecaptchaToken(null)}
                                />
                                {(recaptchaError || submitError) && <p className="recaptcha-error">{submitError || t('error_recaptcha')}</p>}
                            </div>
                        )}

                        <div className="modal-footer">
                            <Button
                                type="primary"
                                shape="round"
                                className="action-btn"
                                loading={submitLoading || phase === 'opening'}
                                onClick={handleOpenClick}
                            >
                                {t('open_your_mystery_box')}
                            </Button>
                        </div>
                    </>
                ) : (
                    // ─── 开箱后 ───────────────────────────────────────────────
                    <>
                        <h2 className="result-title">
                            {t('result_title')}
                            <br />
                            {boxRuleNameMap[boxRuleName as keyof typeof boxRuleNameMap]}
                        </h2>

                        <div className={`prizes-row count-${count}`}>
                            {prizes.map((prize) => (
                                <div key={prize.id} className="prize-card">
                                    <div className="prize-ticket">
                                        <Gift type={prize.type} amount={prize.amount || 0} />
                                    </div>
                                    <p className="prize-name">{prize.name}</p>
                                </div>
                            ))}
                        </div>

                        <p className="prize-note">{t('prize_note')}</p>

                        <div className="modal-footer">
                            <Button type="primary" shape="round" className="action-btn" onClick={onCancel}>
                                {t('ok')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default MysteryBoxModal;
